(() => {
  const modelNames = {
    highland: 'Model 3 Highland',
    legacy: 'Model 3 2017–2023',
  };

  const init = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    root.classList.add('is-enhanced');

    const editionButtons = [...root.querySelectorAll('[data-edition-select]')];
    const editionRegions = [...root.querySelectorAll('[data-edition]')];
    const categoryButtons = [...root.querySelectorAll('[data-category-select]')];
    const categoryPanels = [...root.querySelectorAll('[data-category-panel]')];
    const search = root.querySelector('[data-product-search]');
    const sort = root.querySelector('[data-product-sort]');
    const resultCount = root.querySelector('[data-result-count]');
    const empty = root.querySelector('[data-empty-state]');
    const emptyTitle = empty?.querySelector('[data-empty-title]');
    const emptyCopy = empty?.querySelector('[data-empty-copy]');
    const defaultCategory = categoryButtons[0]?.dataset.categorySelect || '';
    const currency = root.dataset.currency || 'USD';
    const allowed = new Set(Object.keys(modelNames));
    const requested = new URLSearchParams(window.location.search).get('vehicle');
    let saved = '';
    try { saved = sessionStorage.getItem('teseryModel3Edition') || ''; } catch (_) {}
    let edition = allowed.has(requested) ? requested : (allowed.has(saved) ? saved : 'highland');
    let category = defaultCategory;

    const track = (event, data = {}) => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, page_type: 'model_3_mods', selected_model: edition, ...data });
    };

    const cardItem = (card) => ({
      item_id: card?.dataset.productId || '',
      item_name: card?.dataset.productTitle || '',
      item_variant: card?.dataset.variantId || '',
      item_brand: 'Tesery',
      item_category: card?.dataset.category || category,
      item_list_id: card?.dataset.placement || 'catalog',
      item_list_name: card?.dataset.placement === 'top-upgrades' ? 'Model 3 Top Upgrades' : 'Model 3 Mods Catalog',
      index: Number(card?.dataset.rank || 0),
      price: Number(card?.dataset.price || 0) / 100,
      fitment_status: card?.dataset.fitmentConfirmed === 'true' ? 'confirmed' : 'review_required',
      selected_model: edition,
    });

    const trackEcommerce = (event, cards, data = {}) => {
      const items = cards.filter(Boolean).map(cardItem);
      if (!items.length) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({ event, ecommerce: { currency, items, ...data } });
    };

    const panelFor = (key) => categoryPanels.find((panel) => panel.dataset.categoryPanel === key);
    const regionFor = (panel, selectedEdition = edition) => panel?.querySelector(`[data-edition="${selectedEdition}"]`);

    const productCount = (panel, selectedEdition = edition) => {
      const region = regionFor(panel, selectedEdition);
      const grid = region?.querySelector('[data-product-grid]');
      if (grid?.dataset.hydrated === 'true') return grid.querySelectorAll('[data-product-card]').length;
      return region?.querySelector('[data-product-template]')?.content.querySelectorAll('[data-product-card]').length || 0;
    };

    const hydrateGrid = (panel, selectedEdition = edition) => {
      const region = regionFor(panel, selectedEdition);
      const grid = region?.querySelector('[data-product-grid]');
      const template = region?.querySelector('[data-product-template]');
      if (!grid || grid.dataset.hydrated === 'true') return grid;
      if (template) grid.appendChild(template.content.cloneNode(true));
      grid.dataset.hydrated = 'true';
      return grid;
    };

    const currentPanel = () => panelFor(category);
    const currentGrid = () => hydrateGrid(currentPanel());
    const availableCategoryButtons = () => categoryButtons.filter((button) => !button.hidden);

    const updateCategoryAvailability = () => {
      let availableCount = 0;
      categoryButtons.forEach((button) => {
        const panel = panelFor(button.dataset.categorySelect);
        const available = productCount(panel) > 0;
        button.hidden = !available;
        button.setAttribute('aria-disabled', available ? 'false' : 'true');
        if (available) availableCount += 1;
      });
      root.querySelectorAll('[data-active-category-count]').forEach((node) => {
        node.textContent = String(availableCount);
      });
      return availableCount;
    };

    const applyProducts = (eventName = '') => {
      const grid = currentGrid();
      const cards = grid ? [...grid.querySelectorAll('[data-product-card]')] : [];
      const query = (search?.value || '').trim().toLowerCase();
      const sorter = {
        featured: (a, b) => Number(a.dataset.rank) - Number(b.dataset.rank),
        'price-asc': (a, b) => Number(a.dataset.price) - Number(b.dataset.price),
        'price-desc': (a, b) => Number(b.dataset.price) - Number(a.dataset.price),
        title: (a, b) => a.dataset.title.localeCompare(b.dataset.title),
      }[sort?.value || 'featured'];
      cards.sort(sorter).forEach((card) => grid.appendChild(card));
      const visibleCards = [];
      cards.forEach((card) => {
        const match = !query || card.dataset.title.includes(query);
        card.hidden = !match;
        if (match) visibleCards.push(card);
      });
      if (resultCount) resultCount.textContent = String(visibleCards.length);
      if (empty) empty.hidden = visibleCards.length !== 0;
      if (emptyTitle) emptyTitle.textContent = query ? 'No upgrades match this search.' : 'No compatible upgrades are available right now.';
      if (emptyCopy) emptyCopy.textContent = query ? 'Clear the search or choose another compatible upgrade system.' : 'Choose another upgrade system or ask Tesery support for fitment help.';
      if (eventName) track(eventName, {
        category,
        sort_by: sort?.value || 'featured',
        visible_products: visibleCards.length,
        query_length: query.length,
      });
      return { cards, visibleCards };
    };

    const setCategory = (next, options = {}) => {
      const targetButton = categoryButtons.find((button) => button.dataset.categorySelect === next);
      if (!panelFor(next) || targetButton?.hidden) return;
      category = next;
      categoryButtons.forEach((button) => {
        const active = button.dataset.categorySelect === category;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        button.tabIndex = active && !button.hidden ? 0 : -1;
      });
      categoryPanels.forEach((panel) => { panel.hidden = panel.dataset.categoryPanel !== category; });
      if (search) search.value = '';
      if (sort) sort.value = 'featured';
      const { visibleCards } = applyProducts();
      if (options.track !== false) {
        track('model_3_mods_category_view', { category, visible_products: visibleCards.length });
        trackEcommerce('view_item_list', visibleCards);
      }
      if (options.scroll) root.querySelector('[data-catalog]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const setEdition = (next, options = {}) => {
      if (!allowed.has(next)) return;
      edition = next;
      editionButtons.forEach((button) => {
        const active = button.dataset.editionSelect === edition;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      editionRegions.forEach((region) => { region.hidden = region.dataset.edition !== edition; });
      root.querySelectorAll('[data-active-edition]').forEach((node) => { node.textContent = modelNames[edition]; });
      updateCategoryAvailability();
      const currentButton = categoryButtons.find((button) => button.dataset.categorySelect === category);
      const nextCategory = currentButton && !currentButton.hidden ? category : availableCategoryButtons()[0]?.dataset.categorySelect;
      if (nextCategory) setCategory(nextCategory, { track: false });
      try { sessionStorage.setItem('teseryModel3Edition', edition); } catch (_) {}
      const url = new URL(window.location.href);
      url.searchParams.set('vehicle', edition);
      history.replaceState({}, '', url);
      const grid = currentGrid();
      const visibleCards = grid ? [...grid.querySelectorAll('[data-product-card]:not([hidden])')] : [];
      if (options.track !== false) {
        track('model_3_mods_edition_select', { placement: options.placement || 'selector' });
        trackEcommerce('view_item_list', visibleCards);
      }
      if (options.scroll) root.querySelector('[data-top-upgrades]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    editionButtons.forEach((button) => button.addEventListener('click', () => setEdition(button.dataset.editionSelect, {
      scroll: button.dataset.scroll === 'true',
      placement: button.dataset.placement || 'selector',
    })));
    categoryButtons.forEach((button) => button.addEventListener('click', () => setCategory(button.dataset.categorySelect, { scroll: true })));
    categoryButtons.forEach((button) => button.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const buttons = availableCategoryButtons();
      const index = buttons.indexOf(button);
      let nextIndex = index;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = buttons.length - 1;
      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + buttons.length) % buttons.length;
      if (event.key === 'ArrowRight') nextIndex = (index + 1) % buttons.length;
      const nextButton = buttons[nextIndex];
      if (!nextButton) return;
      setCategory(nextButton.dataset.categorySelect);
      nextButton.focus();
    }));

    let searchTimer;
    search?.addEventListener('input', () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => applyProducts('model_3_mods_search'), 220);
    });
    sort?.addEventListener('change', () => applyProducts('model_3_mods_sort'));

    root.addEventListener('submit', async (event) => {
      const form = event.target.closest?.('[data-quick-add]');
      if (!form) return;
      event.preventDefault();
      const button = form.querySelector('button');
      const label = button?.querySelector('span');
      const original = label?.textContent || 'Add to cart';
      if (button) button.disabled = true;
      if (label) label.textContent = 'Adding…';
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form),
        });
        if (!response.ok) throw new Error('Unable to add');
        const item = await response.json();
        if (label) label.textContent = 'Added';
        const cartDrawer = document.querySelector('m-cart-drawer');
        if (typeof cartDrawer?.onCartDrawerUpdate === 'function') cartDrawer.onCartDrawerUpdate();
        if (window.MinimogSettings?.enable_cart_drawer && typeof cartDrawer?.open === 'function') cartDrawer.open(button);
        const cartUpdateEvent = window.MinimogTheme?.pubSubEvents?.cartUpdate;
        if (cartUpdateEvent && typeof window.MinimogEvents?.emit === 'function') window.MinimogEvents.emit(cartUpdateEvent, item);
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        const card = form.closest('[data-product-card]');
        track('model_3_mods_quick_add', {
          product_id: item.product_id,
          variant_id: item.variant_id,
          category: card?.dataset.category || category,
          placement: card?.dataset.placement || 'catalog',
          currency,
          value: Number(card?.dataset.price || 0) / 100,
        });
        trackEcommerce('add_to_cart', [card], { value: Number(card?.dataset.price || 0) / 100 });
        setTimeout(() => { if (label) label.textContent = original; if (button) button.disabled = false; }, 1800);
      } catch (_) {
        const link = form.closest('[data-product-card]')?.querySelector('[data-product-link]');
        if (link) window.location.assign(link.href);
        else {
          if (label) label.textContent = original;
          if (button) button.disabled = false;
        }
      }
    });

    root.addEventListener('click', (event) => {
      const path = event.target.closest?.('[data-category-target]');
      if (path) {
        event.preventDefault();
        const target = path.dataset.categoryTarget;
        const button = categoryButtons.find((candidate) => candidate.dataset.categorySelect === target && !candidate.hidden);
        if (button) setCategory(target, { scroll: true });
        else setCategory(availableCategoryButtons()[0]?.dataset.categorySelect, { scroll: true });
        track('model_3_mods_cta_click', { placement: path.dataset.track || 'build-path', destination: target });
        return;
      }
      const link = event.target.closest?.('[data-product-link]');
      if (link) {
        const card = link.closest('[data-product-card]');
        track('model_3_mods_product_click', {
          product_id: card?.dataset.productId || '',
          variant_id: card?.dataset.variantId || '',
          category: card?.dataset.category || category,
          placement: card?.dataset.placement || 'catalog',
          currency,
          value: Number(card?.dataset.price || 0) / 100,
          fitment_status: card?.dataset.fitmentConfirmed === 'true' ? 'confirmed' : 'review_required',
          destination: link.getAttribute('href'),
        });
        trackEcommerce('select_item', [card]);
        return;
      }
      const tracked = event.target.closest?.('[data-track]');
      if (tracked) track('model_3_mods_cta_click', { placement: tracked.dataset.track, destination: tracked.getAttribute('href') || '' });
    });

    setEdition(edition, { track: false });
    const initialGrid = currentGrid();
    const initialCards = initialGrid ? [...initialGrid.querySelectorAll('[data-product-card]:not([hidden])')] : [];
    trackEcommerce('view_item_list', initialCards);

    const mobileBar = root.querySelector('[data-mobile-bar]');
    if (mobileBar) {
      const hero = root.querySelector('.m3m-hero');
      const finalCta = root.querySelector('.m3m-final');
      const update = () => {
        const afterHero = hero && hero.getBoundingClientRect().bottom < 80;
        const beforeFinal = !finalCta || finalCta.getBoundingClientRect().top > window.innerHeight * 0.75;
        mobileBar.classList.toggle('is-visible', afterHero && beforeFinal);
      };
      window.addEventListener('scroll', update, { passive: true });
      update();
    }
  };

  document.querySelectorAll('[data-model-3-mods]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-model-3-mods]').forEach(init));
})();
